import axios from 'axios';
import { createUri, Api } from './utils';

import type { Method } from 'axios';
import type { Category, Coverage, Manufacturer, Model } from '@/features/redux/slices/designSlice';

export interface AutocompleteResponse {
    id: string;
    manufacturer: string;
    model: string;
}

export interface DeviceDetailResponse {
    model: string;
    tags: string[];
    releaseDates: string[];
    category: string;
    categoryId: number;
}

const API_URL = createUri(Api.Designs, '/designs/V1');

export const dataAPI = axios.create({
    baseURL: API_URL,
});

export const designAPI = (token: string) => {
    const request = async <T>(url: string, method: Method, data?: T) => {
        const resp = await dataAPI.request({
            url,
            data,
            method,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return resp ? resp.data : null;
    };

    const get = async (url: string) => {
        const resp = await request(url, 'GET');

        if (resp) {
            return resp.Item;
        }

        return null;
    };

    const getList = async (url: string) => {
        const resp = await request(url, 'GET');
        return resp && resp.Items ? resp.Items : resp;
    };

    return {
        deviceInfoAutocomplete: async (search: string): Promise<AutocompleteResponse[]> => {
            const resp = await request(`/device-info-autocomplete?modelName=${search}`, 'GET');
            return resp;
        },
        deviceInfoDetail: async (id: string): Promise<DeviceDetailResponse> => {
            const resp = await request(`/device-info-detail?id=${id}`, 'GET');
            return resp;
        },
        getCoverage: async (coverageId: string, language: string): Promise<Coverage> => {
            return await get(
                `/coverage?props=coverageId,languageCode,coverageName,facing,isCase,compatability&coverageId=${coverageId}&languageCode=${language}`
            );
        },
        getModel: async (modelId: string): Promise<Model> => {
            return await get(
                `/model?props=modelId,manufacturerId,coverages,searchTags,model,version,compatability,active,categoryId,deviceId&modelId=${modelId}`
            );
        },
        getModels: async (): Promise<Model[]> => {
            return await getList('/models?props=modelId,model,manufacturerId');
        },
        getManufacturers: async (): Promise<Manufacturer[]> => {
            return await getList('/manufacturers?props=manufacturerId,manufacturerName,active');
        },
        getCoverages: async (language: string): Promise<Coverage[]> => {
            return await getList(
                `/coverages?props=coverageId,coverageName,languageCode,active,compatability&languageCode=${language}`
            );
        },
        postCoverage: async (data: any, method: Method): Promise<any> => {
            return await request('/coverage', method, JSON.stringify(data));
        },
        postManufacturer: async (data: Partial<Manufacturer>): Promise<any> => {
            const { manufacturerId } = data;

            const method = manufacturerId ? 'PUT' : 'POST';

            return await request('/manufacturer', method, JSON.stringify(data));
        },
        postModel: async (data: Partial<Model>): Promise<any> => {
            const { modelId } = data;

            const method = modelId ? 'PUT' : 'POST';

            return await request('/model', method, JSON.stringify(data));
        },
        getCategories: async (): Promise<Category[]> => {
            return await getList('/categories?props=categoryId,categoryName');
        }
    };
};
